import { moveSlidingToggle } from './sliding-toggle.js';
const PAGE_ENTRY_SELECTOR = '[data-shipping-policy-entry]';
const PAGE_ENTRY_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const PAGE_ENTRY_TRANSITIONS = Object.freeze({
  title: {
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 12px, 0)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
    ],
  },
  copy: {
    duration: 600,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 12px, 0)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
    ],
  },
  control: {
    duration: 520,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 8px, 0)', filter: 'blur(4px)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
    ],
  },
});

const documentRoot = document.documentElement;
const shippingPolicyPage = document.querySelector('[data-shipping-policy-view]');
const pageEntryAnimations = [];

const completeShippingPolicyEntry = () => {
  documentRoot.dataset.shippingPolicyEntryState = 'complete';
  pageEntryAnimations.splice(0).forEach((animation) => animation.cancel());
  shippingPolicyPage
    ?.querySelectorAll(PAGE_ENTRY_SELECTOR)
    .forEach((element) => element.classList.remove('is-shipping-policy-entry-active'));
};

const isInitiallyVisible = (element, viewportHeight, headerBottom) => {
  const bounds = element.getBoundingClientRect();

  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.right > 0 &&
    bounds.left < window.innerWidth &&
    bounds.bottom > headerBottom &&
    bounds.top < viewportHeight
  );
};

const startShippingPolicyEntry = () => {
  if (documentRoot.dataset.shippingPolicyEntryState !== 'pending') {
    return;
  }

  if (
    !shippingPolicyPage ||
    window.Shopify?.designMode ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    typeof Element.prototype.animate !== 'function'
  ) {
    completeShippingPolicyEntry();
    return;
  }

  const viewportHeight = window.innerHeight;
  const siteHeader = document.querySelector('.site-header');
  const headerBottom = siteHeader instanceof HTMLElement
    ? Math.max(0, siteHeader.getBoundingClientRect().bottom)
    : 0;
  const candidates = [...shippingPolicyPage.querySelectorAll(PAGE_ENTRY_SELECTOR)];

  candidates.forEach((element) => {
    if (!isInitiallyVisible(element, viewportHeight, headerBottom)) {
      return;
    }

    const transition = PAGE_ENTRY_TRANSITIONS[element.dataset.shippingPolicyEntry];

    if (!transition) {
      return;
    }

    const delay = Number.parseInt(element.dataset.shippingPolicyEntryDelay || '0', 10);
    element.classList.add('is-shipping-policy-entry-active');
    pageEntryAnimations.push(
      element.animate(transition.keyframes, {
        delay: Number.isNaN(delay) ? 0 : delay,
        duration: transition.duration,
        easing: PAGE_ENTRY_EASING,
        fill: 'both',
      }),
    );
  });

  documentRoot.dataset.shippingPolicyEntryState = 'running';

  if (pageEntryAnimations.length === 0) {
    completeShippingPolicyEntry();
    return;
  }

  Promise.allSettled(pageEntryAnimations.map((animation) => animation.finished)).then(
    completeShippingPolicyEntry,
  );
};

window.requestAnimationFrame(startShippingPolicyEntry);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    completeShippingPolicyEntry();
  }
});

/* ─────────────────────────────────────────────────────────
 * EU IMPORT RULES DISCLOSURE STORYBOARD
 *
 *    0ms   toggle label and expand icon respond to the user
 *  180ms   button label and icon finish resolving
 *  260ms   policy content reaches its expanded or collapsed height
 * ───────────────────────────────────────────────────────── */
const DISCLOSURE_MOTION = Object.freeze({
  duration: 260,
  easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
});

const VIEW_MOTION = Object.freeze({
  duration: 260,
  easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  exitDuration: 150,
  enterDuration: 220,
  enterDelay: 30,
  exitEasing: 'cubic-bezier(0.215, 0.61, 0.355, 1)',
  enterEasing: 'cubic-bezier(0.23, 1, 0.32, 1)',
});

const TITLE_MOTION = Object.freeze({
  moveDuration: 260,
  reverseDuration: 250,
  reverseDelay: 25,
  moveEasing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
  wordEnterDuration: 220,
  wordEnterDelay: 35,
  wordExitDuration: 160,
  wordEasing: 'cubic-bezier(0.23, 1, 0.32, 1)',
});

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const page = document.querySelector('[data-shipping-policy-view]');
const switcher = document.querySelector('[data-shipping-policy-switcher]');
const viewStack = page?.querySelector('[data-shipping-policy-views]');
const pageTitle = page?.querySelector('[data-shipping-policy-title]');
const internationalTitleWord = pageTitle?.querySelector('[data-shipping-policy-title-international]');
const baseTitleWords = pageTitle?.querySelector('[data-shipping-policy-title-base]');
const tabs = [...(switcher?.querySelectorAll('[data-shipping-policy-tab]') || [])];
const panels = [...(page?.querySelectorAll('[data-shipping-policy-panel]') || [])];
let viewSwitching = false;

if (
  page &&
  switcher &&
  viewStack &&
  pageTitle &&
  internationalTitleWord &&
  baseTitleWords &&
  tabs.length === 2 &&
  panels.length === 2
) {
  moveSlidingToggle(switcher.querySelector('.shipping-policy-switcher__indicator'), page.dataset.shippingPolicyView === 'international' ? 1 : 0, false);
  const panelFor = (view) => panels.find((panel) => panel.dataset.shippingPolicyPanel === view);
  const titleFor = (view) => {
    const baseTitle = baseTitleWords.textContent.trim() || 'Shipping policy';
    const internationalTitle = internationalTitleWord.textContent.trim();

    return view === 'international' && internationalTitle
      ? `${internationalTitle} ${baseTitle}`
      : baseTitle;
  };

  const setTitleState = (view) => {
    internationalTitleWord.hidden = view !== 'international';
    pageTitle.setAttribute('aria-label', titleFor(view));
    pageTitle.removeAttribute('style');
    baseTitleWords.removeAttribute('style');
    internationalTitleWord.removeAttribute('style');
  };

  const animateTitle = (view) => {
    const revealing = view === 'international';
    const startTitleHeight = pageTitle.getBoundingClientRect().height;
    const startBaseRect = baseTitleWords.getBoundingClientRect();

    internationalTitleWord.hidden = !revealing;
    const targetTitleHeight = pageTitle.getBoundingClientRect().height;
    const targetBaseRect = baseTitleWords.getBoundingClientRect();

    if (!revealing) {
      internationalTitleWord.hidden = false;
    }

    pageTitle.style.height = `${startTitleHeight}px`;
    pageTitle.style.overflow = 'visible';

    const deltaX = targetBaseRect.left - startBaseRect.left;
    const deltaY = targetBaseRect.top - startBaseRect.top;
    const inverseTransform = `translate3d(${-deltaX}px, ${-deltaY}px, 0)`;
    const targetTransform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
    const movingFrames = revealing
      ? [
          { transform: inverseTransform, filter: 'blur(0px)' },
          { transform: `translate3d(${-deltaX * 0.45}px, ${-deltaY * 0.45}px, 0)`, filter: 'blur(0.8px)', offset: 0.55 },
          { transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
        ]
      : [
          { transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
          { transform: `translate3d(${deltaX * 0.55}px, ${deltaY * 0.55}px, 0)`, filter: 'blur(0.8px)', offset: 0.55 },
          { transform: targetTransform, filter: 'blur(0px)' },
        ];
    const moveOptions = {
      duration: revealing ? TITLE_MOTION.moveDuration : TITLE_MOTION.reverseDuration,
      delay: revealing ? 0 : TITLE_MOTION.reverseDelay,
      easing: TITLE_MOTION.moveEasing,
      fill: 'both',
    };
    const baseAnimation = baseTitleWords.animate(movingFrames, moveOptions);
    const heightAnimation = pageTitle.animate(
      [{ height: `${startTitleHeight}px` }, { height: `${targetTitleHeight}px` }],
      moveOptions,
    );
    const internationalAnimation = internationalTitleWord.animate(
      revealing
        ? [
            { opacity: 0, transform: 'translate3d(-0.18em, 0.08em, 0) scale(0.985)', filter: 'blur(4px)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', filter: 'blur(0px)' },
          ]
        : [
            { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)', filter: 'blur(0px)' },
            { opacity: 0, transform: 'translate3d(-0.12em, -0.04em, 0) scale(0.985)', filter: 'blur(4px)' },
          ],
      {
        duration: revealing ? TITLE_MOTION.wordEnterDuration : TITLE_MOTION.wordExitDuration,
        delay: revealing ? TITLE_MOTION.wordEnterDelay : 0,
        easing: TITLE_MOTION.wordEasing,
        fill: 'both',
      },
    );
    const animations = [baseAnimation, heightAnimation, internationalAnimation];

    return Promise.allSettled(animations.map((animation) => animation.finished)).then(() => {
      animations.forEach((animation) => animation.cancel());
      setTitleState(view);
    });
  };

  const setControlState = (view) => {
    page.dataset.shippingPolicyView = view;
    page.classList.toggle('shipping-policy--domestic', view === 'domestic');
    page.classList.toggle('shipping-policy--international', view === 'international');
    switcher.dataset.shippingPolicySwitcher = view;
    pageTitle.setAttribute('aria-label', titleFor(view));

    tabs.forEach((tab) => {
      const active = tab.dataset.shippingPolicyTab === view;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });
  };

  const finishViewSwitch = (currentPanel, nextPanel, view) => {
    currentPanel.hidden = true;
    currentPanel.inert = true;
    currentPanel.removeAttribute('style');
    nextPanel.hidden = false;
    nextPanel.inert = false;
    nextPanel.removeAttribute('style');
    viewStack.removeAttribute('style');
    delete viewStack.dataset.shippingPolicySwitching;
    setTitleState(view);
    viewSwitching = false;
    document.dispatchEvent(new CustomEvent('shipping-policy:viewchange', { detail: { view } }));
  };

  const switchView = (view, { animate = true } = {}) => {
    const currentView = page.dataset.shippingPolicyView;

    if (viewSwitching || view === currentView) {
      return;
    }

    const currentPanel = panelFor(currentView);
    const nextPanel = panelFor(view);

    if (!currentPanel || !nextPanel) {
      return;
    }

    moveSlidingToggle(switcher.querySelector('.shipping-policy-switcher__indicator'), view === 'international' ? 1 : 0, animate);
    viewSwitching = true;
    const currentHeight = currentPanel.getBoundingClientRect().height;
    viewStack.style.height = `${currentHeight}px`;
    viewStack.dataset.shippingPolicySwitching = 'true';
    currentPanel.style.position = 'absolute';
    currentPanel.style.inset = '0';
    nextPanel.hidden = false;
    nextPanel.inert = false;
    const nextHeight = nextPanel.scrollHeight;
    setControlState(view);

    if (!animate || reducedMotion.matches || typeof viewStack.animate !== 'function') {
      finishViewSwitch(currentPanel, nextPanel, view);
      return;
    }

    const heightAnimation = viewStack.animate(
      [{ height: `${currentHeight}px` }, { height: `${nextHeight}px` }],
      { duration: VIEW_MOTION.duration, easing: VIEW_MOTION.easing },
    );
    const outgoingAnimation = currentPanel.animate(
      [
        { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
        { opacity: 0, transform: 'translateY(-6px)', filter: 'blur(3px)' },
      ],
      { duration: VIEW_MOTION.exitDuration, easing: VIEW_MOTION.exitEasing, fill: 'forwards' },
    );
    const incomingAnimation = nextPanel.animate(
      [
        { opacity: 0, transform: 'translateY(6px)', filter: 'blur(3px)' },
        { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
      ],
      {
        duration: VIEW_MOTION.enterDuration,
        delay: VIEW_MOTION.enterDelay,
        easing: VIEW_MOTION.enterEasing,
        fill: 'both',
      },
    );
    const titleAnimation = animateTitle(view);

    Promise.allSettled([
      heightAnimation.finished,
      outgoingAnimation.finished,
      incomingAnimation.finished,
      titleAnimation,
    ]).then(() => {
      // Clear filled animation states before either panel is reused.
      heightAnimation.cancel();
      outgoingAnimation.cancel();
      incomingAnimation.cancel();
      finishViewSwitch(currentPanel, nextPanel, view);
    });
  };

  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => {
      completeShippingPolicyEntry();
      switchView(tab.dataset.shippingPolicyTab);
    });
    tab.addEventListener('keydown', (event) => {
      let nextIndex;

      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      tabs[nextIndex].focus();
      switchView(tabs[nextIndex].dataset.shippingPolicyTab, { animate: false });
    });
  });
}

const root = document.querySelector('[data-shipping-policy-disclosure]');
const toggle = root?.querySelector('[data-shipping-policy-toggle]');
const content = root?.querySelector('[data-shipping-policy-rules]');
const labels = toggle?.querySelector('.shipping-policy__toggle-labels');
const moreLabel = toggle?.querySelector('.shipping-policy__toggle-label--more');
const lessLabel = toggle?.querySelector('.shipping-policy__toggle-label--less');
const iconPaths = [...(toggle?.querySelectorAll('[data-path-show][data-path-hide]') || [])];

if (root && toggle && content) {
  const supportsPathMorph = window.CSS?.supports?.('d', 'path("M0 0L1 1")') === true;
  let animation;

  const syncLabelWidths = () => {
    if (!labels || !moreLabel || !lessLabel) {
      return;
    }

    labels.style.setProperty('--shipping-toggle-label-width-more', `${moreLabel.scrollWidth}px`);
    labels.style.setProperty('--shipping-toggle-label-width-less', `${lessLabel.scrollWidth}px`);
  };

  const setFallbackIconState = (expanded) => {
    if (supportsPathMorph) {
      return;
    }

    iconPaths.forEach((path) => {
      path.setAttribute('d', expanded ? path.dataset.pathHide : path.dataset.pathShow);
    });
  };

  const setToggleState = (expanded) => {
    toggle.setAttribute('aria-expanded', String(expanded));
    toggle.setAttribute('aria-label', expanded ? 'Collapse EU import rules' : 'Expand EU import rules');
    content.dataset.shippingPolicyExpanded = String(expanded);
    content.toggleAttribute('inert', !expanded);
    content.setAttribute('aria-hidden', String(!expanded));
    setFallbackIconState(expanded);
  };

  const finishAtState = (expanded) => {
    animation = undefined;
    content.style.height = expanded ? 'auto' : '0px';
  };

  const animateToState = (expanded) => {
    const startHeight = content.getBoundingClientRect().height;

    animation?.cancel();
    animation = undefined;

    content.style.height = `${startHeight}px`;
    const endHeight = expanded ? content.scrollHeight : 0;
    setToggleState(expanded);

    if (reducedMotion.matches || typeof content.animate !== 'function' || Math.abs(endHeight - startHeight) < 1) {
      finishAtState(expanded);
      return;
    }

    animation = content.animate(
      [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
      {
        duration: DISCLOSURE_MOTION.duration,
        easing: DISCLOSURE_MOTION.easing,
      },
    );

    animation.addEventListener('finish', () => finishAtState(expanded), { once: true });
  };

  content.style.height = '0px';
  setToggleState(false);
  root.dataset.shippingPolicyReady = 'true';
  syncLabelWidths();
  document.fonts?.ready.then(syncLabelWidths);
  document.addEventListener('shipping-policy:viewchange', (event) => {
    if (event.detail?.view === 'international') {
      window.requestAnimationFrame(syncLabelWidths);
    }
  });

  toggle.addEventListener('click', () => {
    animateToState(toggle.getAttribute('aria-expanded') !== 'true');
  });
}
